import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  /** Renders the trigger element. `open`/`toggle` let the trigger reflect open state if needed. */
  trigger: (state: { open: boolean; toggle: () => void }) => React.ReactNode;
  /** Renders the menu content. Call `close()` from inside item click handlers to dismiss it. */
  children: (close: () => void) => React.ReactNode;
  /** Which edge of the trigger the menu's corresponding edge aligns to. */
  align?: "left" | "right";
  className?: string;
  menuClassName?: string;
}

// Dropdown/action menus positioned with plain `absolute` get clipped whenever their row
// sits near the bottom of a scrolling table/list — the menu renders past the visible area
// (or inside a clipped `overflow-x-auto` ancestor, which the CSS spec forces to also clip
// vertically) with nothing to auto-scroll it into view. This renders the menu into a portal
// on `document.body` with `position: fixed`, computed from the trigger's own bounding box,
// so it's never clipped by any ancestor and flips above the trigger when there isn't enough
// room below.
export const Popover: React.FC<PopoverProps> = ({ trigger, children, align = "right", className, menuClassName }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ position: "fixed", visibility: "hidden" });

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  const updatePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const gap = 6;

    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;

    const next: React.CSSProperties = {
      position: "fixed",
      zIndex: 9999,
      visibility: "visible",
      maxHeight: `calc(100vh - ${gap * 2}px)`,
      overflowY: "auto",
    };

    if (openUpward) {
      next.bottom = window.innerHeight - rect.top + gap;
    } else {
      next.top = rect.bottom + gap;
    }

    if (align === "right") {
      next.right = window.innerWidth - rect.right;
    } else {
      next.left = rect.left;
    }

    setStyle(next);
  }, [align]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    // Recompute once more after the menu has actually painted, so `menuHeight` above
    // (0 on the very first frame) reflects its real size for the flip-up check.
    const raf = requestAnimationFrame(updatePosition);

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, close]);

  return (
    <div ref={triggerRef} className={className ? className : "inline-block"}>
      {trigger({ open, toggle })}
      {open &&
        createPortal(
          <div ref={menuRef} style={style} className={menuClassName || "bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[160px]"}>
            {children(close)}
          </div>,
          document.body
        )}
    </div>
  );
};
